import { isC3Experience } from "../../../shared/util/C3Utils";
define((require) => {
    "use strict";

    const Backdrop                    = require("shared/ui/experience/Backdrop");
    const ColorPicker                 = require("shared/services/ColorPicker");
    const CompactImageLibraryView     = require("ui/editor/imageLibrary/CompactImageLibraryView");
    const EditorEvents                = require("shared/services/EditorEvents");
    const ImageCollection             = require("data/ImageCollection");
    const ImageLibrarySortingEnum     = require("ui/imageLibrary/ImageLibrarySortingEnum");
    const ImageLibrarySortingProvider = require("ui/imageLibrary/ImageLibrarySortingProvider");
    const ImageLibrarySourceEnum      = require("shared/data/images/ImageLibrarySourceEnum");
    const ImprovedView                = require("editor/zbone/ImprovedView");
    const template                    = require("ui/experience/BackdropView.tpl");

    /**
     * View for editing the backdrop options.
     *
     * @class BackdropView
     * @constructor
     *
     * @cfg {Backdrop} model The Backdrop model.
     */
    return ImprovedView.extend({

        template,

        templateHelpers() {
            return {
                isScene7IntegrationAuthorized: this.options.integrationsCollection && this.options.integrationsCollection.isScene7IntegrationAuthorized(),
                isC3Experience: isC3Experience()
            };
        },

        className: "backdrop-options",

        regions: {
            imageLibraryRegion: ".backdrop-image-library-region"
        },

        viewScope() {
            return {
                viewModel: this.options.viewModel
            };
        },

        events: {
            "click @ui.cancelButton": "close",
            "click @ui.okButton": "_saveBackdropSettings"
        },

        ui: {
            imageLibrary: ".backdrop-image-library-region",
            colorPicker: ".backdrop-color-picker",
            colorInput: ".color-input",
            colorBox: ".color-box",
            colorLibrary: ".color-library",
            illustrationBox: ".illustration-box",
            okButton: ".ok-btn",
            cancelButton: ".cancel-btn",
            containerBox: ".container-box",
            experienceBox: ".experience-box"
        },

        bindings: {
            ".color-input": {
                observe: "color",
                onGet() {
                    const color = this.model.getColor();

                    return color === "transparent" ? "" : color;
                },

                onSet(val) {
                    return val || "transparent";
                }
            },
            "input[name=type]": "type",
            "input[name=fit]": "fit"
        },

        modelEvents: {
            "change:type": "_updateTypeView _trackTypeChange",
            "change:fit": "_updateFitView _trackFitChange",
            "change": "_updateIllustration"
        },

        initialize() {
            const tempModel = this.model.clone();

            // Save for later uses when users click the OK button
            this.backdropModel = this.model;

            // Hack: assigning experience in order to get the old background color. @see {@link Backdrop#getColor}
            tempModel.experience = this.model.experience;
            this.model = tempModel;
        },

        /**
         * Toggle the imageLibrary and colorPicker depending on the backdrop type
         * Also send an analytics events to the server
         */
        _updateTypeView() {
            const isTypeImage = this.model.isTypeImage();

            this.ui.imageLibrary.toggle(isTypeImage);
            this.ui.colorPicker.toggle(!isTypeImage);
        },

        _trackTypeChange() {
            const eventProperties = {
                updatedProperty: "type",
                updatedValue: this.model.get("type"),
                experienceId: this.model.experience.get("id"),
                experienceVersion: this.model.experience.get("version"),
                experiencePushedVersion: this.model.get("pushedVersion"),
                triggerOrigin: "BackdropTypeRadioButton"
            };

            EditorEvents.trigger(EditorEvents.constants.UPDATE_BACKDROP, eventProperties);
        },

        /**
         * Toggle the container and experience class depending on the backdrop fit
         * Also send an analytics events to the server
         */
        _updateFitView() {
            const isFitContainer = this.model.isFitContainer();

            this.ui.illustrationBox.toggleClass("container", isFitContainer);
            this.ui.illustrationBox.toggleClass("experience", !isFitContainer);
        },

        _trackFitChange() {
            const eventProperties = {
                updatedProperty: "fit",
                updatedValue: this.model.get("fit"),
                experienceId: this.model.experience.get("id"),
                experienceVersion: this.model.experience.get("version"),
                experiencePushedVersion: this.model.get("pushedVersion"),
                triggerOrigin: "BackdropFitRadioButton"
            };

            EditorEvents.trigger(EditorEvents.constants.UPDATE_BACKDROP, eventProperties);
        },

        /**
         * Set the backdrop image when an image is selected in the image library
         *
         * @param {ImageModel} imageModel The selected image, which is an instance of {@link ImageModel}.
         */
        _imageSelectedHandler(imageModel) {
            this.model.set("image", {
                id: imageModel.get("id"),
                url: imageModel.get("url"),
                width: imageModel.get("width"),
                height: imageModel.get("height"),
                supportsResize: imageModel.get("supportsResize")
            });
        },

        /**
         * Update the illustration whenever backdrop fit or type changes. Note it will remove the previous illustration.
         */
        _updateIllustration() {
            const container = this.model.isFitContainer() ? this.ui.containerBox : this.ui.experienceBox;

            // Remove existing backgrounds
            this.ui.containerBox.add(this.ui.experienceBox).css({
                "background-image": "",
                "background-color": ""
            });

            switch (this.model.get("type")) {
                case Backdrop.TYPE.IMAGE:
                    container.css("background-image", "url(" + (this.model.getImageURL() || "") + ")");
                    break;

                case Backdrop.TYPE.COLOR:
                    container.css("background-color", this.model.getColor());
                    break;

                default:
                    break;
            }
        },

        /**
         * Render the backdrop view, set the initial fit and type and initialize the color picker.
         * Also sends an analytic event to the server.
         */
        onRender() {
            this._updateFitView();
            this._updateTypeView();
            this._updateIllustration();

            this._backgroundColorPicker = new ColorPicker({
                color: this.model.getColor(),
                input: this.ui.colorInput,
                colorBox: this.ui.colorBox,
                library: this.ui.colorLibrary,
                includeTransparency: true,
                tracking: "colorpicker:box"
            });
        },

        /**
         * Shows the image library and listen on the image selection event. This should not be called onRender
         * because we need to respect the Marionette life cycle of showing view.
         */
        onShow() {
            if(isC3Experience()) return;
            const {viewModel} = this.viewScope;
            const currentBackdropImage = this.model.get("image");

            /**
             * TODO: Re-use the ImageLibrarySortingProvider from the viewModel
             *
             * Currently new ImageLibrarySortingProvider is used for Backdrop due to MOS-3426
             * When sources are enabled for the backdrop the ImageLibrarySortingProvider
             * should instead be provided using:
             * <pre>
             *   const ImageLibrarySortingProvider = this.viewScope.viewModel.get("imageLibrarySorting");
             * </pre>
             */
            let creatorSorting = ImageLibrarySortingEnum.without(ImageLibrarySortingEnum.MOST_VIEWED);

            if (viewModel.get("imageViewedEventsEnabled")) {
                creatorSorting = ImageLibrarySortingEnum.without(ImageLibrarySortingEnum.ALPHABETICAL);
            }

            const imageLibrarySorting = ImageLibrarySortingProvider.provide({
                sources: [
                    {source: ImageLibrarySourceEnum.CREATOR, sorting: creatorSorting}]
            });

            const collection = new ImageCollection();
            const imageLibrary = new CompactImageLibraryView({
                collection: collection,
                selectedImageId: currentBackdropImage && currentBackdropImage.id,
                autoSelection: true,
                project: viewModel.get("project"),
                imageLibrarySorting,
                itemViewOptions: {
                    imageTaggingEnabled: viewModel.get("imageTaggingEnabled"),
                    inventoryEnabled: viewModel.get("inventoryEnabled")
                },
                sortingPanelOptions: {
                    showSources: false
                }
            });

            collection.query(imageLibrarySorting.getSelectedSorting().query);

            this.listenTo(imageLibrary, "image:selected", this._imageSelectedHandler);
            this.imageLibraryRegion.show(imageLibrary);
        },

        /**
         * Save all the backdrop settings for an experience.
         */
        _saveBackdropSettings() {
            this.close();

            const modelType = this.model.get("type");
            const options = {
                type: this.model.get("type"),
                fit: this.model.get("fit")
            };
            let propertyToUnset;

            switch (modelType) {
                case Backdrop.TYPE.IMAGE:
                    options.image = this.model.get("image");
                    propertyToUnset = "color";
                    break;

                case Backdrop.TYPE.COLOR:
                    options.color = this.model.getColor();
                    propertyToUnset = "image";
                    break;

                default:
                    break;
            }

            this.backdropModel.set(options);

            // Remember to unset the previous selected property. See MOS-2736.
            if (propertyToUnset) {
                this.backdropModel.unset(propertyToUnset, {silent: true});
            }

            const eventProperties = {
                fit: this.model.get("fit"),
                type: this.model.get("type"),
                experienceId: this.model.experience.get("id"),
                experienceVersion: this.model.experience.get("version"),
                experiencePushedVersion: this.model.get("pushedVersion"),
                triggerOrigin: "BackdropOptionsModalOkButton"
            };

            EditorEvents.trigger(EditorEvents.constants.SAVE_BACKDROP, eventProperties);
        },

        /**
         * On close this view, we remove the references to the color picker, so it can be garbage collected.
         */
        onClose() {
            if (this._backgroundColorPicker) {
                this._backgroundColorPicker.dispose();
            }
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/BackdropView.js